const API_BASE_URL = process.env.REACT_APP_API_URL;

// Fetch active HRV properties for public display
export async function getActiveHRVProperties() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hrv/active`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Check if response is HTML (server error page)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('API Call - Received HTML response instead of JSON');
      throw new Error('Server connection error. Please ensure the backend server is running on port 5000.');
    }

    if (!response.ok) {
      let errorMessage = 'Failed to fetch HRV properties';
      try {
        const error = await response.json();
        console.error('API Call - Get HRV properties error response:', error);
        errorMessage = error.message || errorMessage;
      } catch (parseError) {
        console.error('API Call - Failed to parse error response:', parseError);
        errorMessage = `Server error (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    let result;
    try {
      result = await response.json();
      // Data is already filtered and sorted by the backend
      return result;
    } catch (parseError) {
      console.error('API Call - Failed to parse HRV properties response:', parseError);
      throw new Error('Invalid server response format');
    }
  } catch (error) {
    console.error('API Call - Get HRV properties fetch error:', error);
    
    // Provide user-friendly error messages
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Unable to connect to server. Please check if the backend is running.');
    }
    
    throw error;
  }
}

// Get all HRV properties (for admin use)
export async function getAllHRVProperties() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hrv`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch HRV properties');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Get all HRV properties error:', error);
    throw error;
  }
}

// Get single HRV property by ID
export async function getHRVPropertyById(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hrv/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch HRV property');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Get HRV property error:', error);
    throw error;
  }
}

// Create new HRV property
export async function createHRVProperty(propertyData) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hrv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(propertyData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create HRV property');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Create HRV property error:', error);
    throw error;
  }
}

// Update HRV property
export async function updateHRVProperty(id, propertyData) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hrv/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(propertyData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update HRV property');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Update HRV property error:', error);
    throw error;
  }
}

// Delete/Deactivate HRV property
export async function deleteHRVProperty(id) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hrv/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete HRV property');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Delete HRV property error:', error);
    throw error;
  }
}

// Update HRV properties order
export async function updateHRVOrder(hrvOrder) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hrv/update-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        hrvOrder
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update HRV order');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call - Update HRV order error:', error);
    throw error;
  }
}